import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { publicApi, quotes as quotesApi } from "@/lib/api";
import { formatMoney, statusLabel } from "@/lib/format";
import { RivetMark } from "@/components/RivetLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { IDS } from "@/constants/testIds";
import { CheckCircle2, MessageSquare, XCircle, Mail, Phone, Globe, MapPin, Loader2, Printer, Wallet, CalendarClock, ShieldCheck } from "lucide-react";
import { format } from "date-fns";

const DECLINE_REASONS = [
  { value: "price", label: "Price is too high" },
  { value: "timing", label: "Timing doesn't work" },
  { value: "scope", label: "Scope isn't right" },
  { value: "found_alternative", label: "Found another provider" },
  { value: "other", label: "Other" },
];

export default function PublicQuote() {
  const { publicId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [displayCurrency, setDisplayCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAccept, setShowAccept] = useState(false);
  const [showModify, setShowModify] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [acceptName, setAcceptName] = useState("");
  const [modifyFeedback, setModifyFeedback] = useState("");
  const [declineReason, setDeclineReason] = useState("price");
  const [declineFeedback, setDeclineFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [depositBusy, setDepositBusy] = useState(false);
  const [scheduleSlot, setScheduleSlot] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const load = useCallback(async (currency) => {
    setLoading(true);
    try {
      const { data } = await publicApi.view(publicId, currency);
      setQuote(data);
      setDisplayCurrency(data.currency);
      setAcceptName((n) => n || data.customer_name || "");
    } catch (e) {
      toast.error("Quote not found");
    } finally {
      setLoading(false);
    }
  }, [publicId]);

  useEffect(() => {
    load();
    publicApi.currencies().then(({ data }) => setCurrencies(data));
  }, [publicId, load]);

  // Auto-trigger print dialog when opened with ?print=1 (from tradie "PDF" button)
  useEffect(() => {
    if (!quote) return;
    if (searchParams.get("print") === "1") {
      const t = setTimeout(() => {
        try { window.print(); } catch { /* noop */ }
      }, 700);
      return () => clearTimeout(t);
    }
  }, [quote, searchParams]);

  // Handle return from Stripe deposit checkout
  useEffect(() => {
    const dep = searchParams.get("deposit");
    const sess = searchParams.get("session_id");
    if (dep === "success" && sess) {
      toast.success("Payment received — verifying…");
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const { data } = await publicApi.depositStatus(sess);
          if (data.payment_status === "paid") {
            clearInterval(iv);
            toast.success("Deposit paid. Thanks!");
            searchParams.delete("deposit"); searchParams.delete("session_id");
            setSearchParams(searchParams, { replace: true });
            await load(displayCurrency);
          }
        } catch { /* keep polling */ }
        if (tries >= 15) { clearInterval(iv); load(displayCurrency); }
      }, 2000);
      return () => clearInterval(iv);
    }
    if (dep === "cancel") {
      toast.info("Deposit cancelled — you can pay it later.");
      searchParams.delete("deposit");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId]);

  const changeCurrency = (code) => {
    setDisplayCurrency(code);
    load(code);
  };

  const handleAccept = async () => {
    if (!acceptName.trim()) { toast.error("Please confirm your name"); return; }
    setSubmitting(true);
    try {
      const { data } = await publicApi.accept(publicId, acceptName.trim());
      toast.success("Quote accepted. Thank you!");
      setShowAccept(false);
      if (data?.deposit_required) {
        // Auto-launch deposit checkout
        await launchDeposit();
      } else {
        await load(displayCurrency);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
    } finally { setSubmitting(false); }
  };

  const launchDeposit = async () => {
    setDepositBusy(true);
    try {
      const { data } = await publicApi.depositCheckout(publicId);
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Could not start deposit payment");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start deposit payment");
    } finally { setDepositBusy(false); }
  };

  const submitSchedule = async () => {
    if (!scheduleSlot) { toast.error("Pick a date & time"); return; }
    setScheduleBusy(true);
    try {
      // Convert local datetime input value ("2026-03-01T10:00") to ISO with tz
      const iso = new Date(scheduleSlot).toISOString();
      await publicApi.schedule(publicId, iso, scheduleNotes || null);
      toast.success("Time slot sent. They'll confirm shortly.");
      setScheduleNotes("");
      await load(displayCurrency);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule");
    } finally { setScheduleBusy(false); }
  };
  const handleModify = async () => {
    if (!modifyFeedback.trim()) { toast.error("Add what you'd like changed"); return; }
    setSubmitting(true);
    try {
      await publicApi.modify(publicId, modifyFeedback.trim());
      toast.success("Modification requested. The business will follow up.");
      setShowModify(false); setModifyFeedback("");
      await load(displayCurrency);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };
  const handleDecline = async () => {
    setSubmitting(true);
    try {
      await publicApi.decline(publicId, declineReason, declineFeedback || null);
      toast.success("Response sent. Thanks for letting them know.");
      setShowDecline(false); setDeclineFeedback("");
      await load(displayCurrency);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F1F5F9]">
        <Loader2 className="animate-spin text-[#2563EB]" size={28}/>
      </div>
    );
  }
  if (!quote) return null;

  const responded = ["accepted", "declined", "modification_requested"].includes(quote.status);

  return (
    <div data-testid={IDS.publicQuote.page} className="min-h-screen bg-[#F1F5F9] text-[#001F3F] pb-32">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {quote.business.logo_url ? (
              <img src={quote.business.logo_url} alt={quote.business.name} className="h-10 w-10 rounded-md object-cover"/>
            ) : (
              <RivetMark size={40}/>
            )}
            <div className="min-w-0">
              <div className="text-sm font-bold tracking-tight text-[#001F3F] truncate" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
                {quote.business.name}
              </div>
              {quote.business.tagline && (
                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#64748B] truncate">{quote.business.tagline}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.print()}
              data-testid="print-quote-btn"
              title="Save as PDF or print"
              className="no-print h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center text-[#001F3F] hover:bg-slate-100"
            >
              <Printer size={15} />
            </button>
            <Select value={displayCurrency} onValueChange={changeCurrency}>
              <SelectTrigger data-testid={IDS.publicQuote.currencySelect} className="w-[100px] h-9 border-slate-200 text-xs no-print">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2563EB]">
          Quote · {quote.quote_number} · v{quote.version}
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight text-[#001F3F]" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
          {quote.title}
        </h1>
        <div className="mt-2 text-sm text-[#334155]">Prepared for <span className="font-semibold">{quote.customer_name}</span></div>
        {quote.description && <p className="mt-4 text-[#334155] leading-relaxed">{quote.description}</p>}

        {/* Photo gallery */}
        {quote.photos && quote.photos.length > 0 && (
          <div data-testid={IDS.publicQuote.photoGallery} className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {quote.photos.map((p) => (
              <a
                key={p.id}
                href={quotesApi.publicPhotoUrl(publicId, p.id)}
                target="_blank"
                rel="noreferrer"
                className="rounded-md overflow-hidden border border-slate-200 bg-slate-50 block hover:opacity-90 transition-opacity"
              >
                <div className="aspect-square">
                  <img
                    src={quotesApi.publicPhotoUrl(publicId, p.id)}
                    alt={p.caption || p.filename || "photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                {p.caption && (
                  <div
                    data-testid={IDS.publicQuote.photoCaption(p.id)}
                    className="px-2.5 py-2 bg-white border-t border-slate-200 text-[11px] leading-snug text-[#334155]"
                  >
                    {p.caption}
                  </div>
                )}
              </a>
            ))}
          </div>
        )}

        {/* Deposit banner — visible if required */}
        {quote.deposit_required && (
          <div
            data-testid={IDS.publicQuote.depositBanner}
            className={`mt-6 rounded-lg border p-5 ${
              quote.deposit_status === "paid"
                ? "border-emerald-200 bg-emerald-50 text-[#065F46]"
                : "border-blue-200 bg-blue-50 text-[#1E3A8A]"
            }`}
          >
            <div className="flex items-start gap-3">
              {quote.deposit_status === "paid"
                ? <ShieldCheck className="shrink-0 mt-0.5" size={20} />
                : <Wallet className="shrink-0 mt-0.5" size={20} />}
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold">
                  {quote.deposit_status === "paid" ? "Deposit received" : "Deposit required"}
                </div>
                <div className="mt-1 text-sm">
                  {quote.deposit_status === "paid" ? (
                    <>{formatMoney(quote.deposit_amount || 0, quote.currency)} paid on {quote.deposit_paid_at ? format(new Date(quote.deposit_paid_at), "MMM d, yyyy") : "—"}.</>
                  ) : (
                    <>
                      A <b>{Number(quote.deposit_percentage).toFixed(0)}%</b> deposit
                      (<b>{formatMoney((quote.total || 0) * (quote.deposit_percentage || 0) / 100, quote.currency)}</b>)
                      is required to lock in this quote.
                    </>
                  )}
                </div>
                {quote.status === "accepted" && quote.deposit_status !== "paid" && (
                  <Button
                    data-testid={IDS.publicQuote.depositPayBtn}
                    onClick={launchDeposit}
                    disabled={depositBusy}
                    className="mt-3 h-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  >
                    {depositBusy && <Loader2 className="animate-spin mr-2" size={14} />}
                    Pay {formatMoney((quote.total || 0) * (quote.deposit_percentage || 0) / 100, quote.currency)} deposit
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scheduler — visible after accept */}
        {quote.status === "accepted" && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-md bg-[#EFF6FF] text-[#2563EB] flex items-center justify-center shrink-0">
                <CalendarClock size={18} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
                  {quote.scheduled_slot_start ? "Requested time" : "Pick a time to start"}
                </div>
                {quote.scheduled_slot_start ? (
                  <>
                    <div className="mt-1 text-sm font-semibold text-[#001F3F]">
                      {format(new Date(quote.scheduled_slot_start), "EEE, MMM d, yyyy · HH:mm")}
                    </div>
                    {quote.scheduled_slot_notes && (
                      <div className="mt-1 text-xs text-[#64748B]">{quote.scheduled_slot_notes}</div>
                    )}
                  </>
                ) : (
                  <div className="mt-3 grid gap-3 sm:grid-cols-[1fr,1fr,auto] items-end">
                    <div>
                      <Label className="text-[11px] uppercase tracking-widest text-[#64748B]">Date & time</Label>
                      <Input
                        data-testid={IDS.publicQuote.schedulerDate}
                        type="datetime-local"
                        value={scheduleSlot}
                        onChange={(e) => setScheduleSlot(e.target.value)}
                        className="mt-1 h-10 border-slate-200"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-widest text-[#64748B]">Notes (optional)</Label>
                      <Input
                        value={scheduleNotes}
                        onChange={(e) => setScheduleNotes(e.target.value)}
                        placeholder="e.g. gate code, side entrance"
                        className="mt-1 h-10 border-slate-200"
                      />
                    </div>
                    <Button
                      data-testid={IDS.publicQuote.schedulerSubmit}
                      onClick={submitSchedule}
                      disabled={scheduleBusy}
                      className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white h-10"
                    >
                      {scheduleBusy && <Loader2 className="animate-spin mr-2" size={14} />}
                      Request time
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {responded && (
          <div className={`mt-5 rounded-lg border p-4 ${
            quote.status === "accepted" ? "border-green-200 bg-green-50 text-[#065F46]" :
            quote.status === "declined" ? "border-red-200 bg-red-50 text-[#991B1B]" :
            "border-amber-200 bg-amber-50 text-[#92400E]"
          }`}>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold">Status</div>
            <div className="mt-1 text-sm font-semibold">{statusLabel(quote.status)}</div>
          </div>
        )}

        {/* Line items */}
        <div className="mt-8 rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
            Items
          </div>
          <div className="divide-y divide-slate-100">
            {quote.line_items.map((li, i) => (
              <div key={i} className="px-5 py-4 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#001F3F]">{li.description}</div>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    {li.quantity} {li.unit || "ea"} × {formatMoney(li.unit_price, quote.currency)}
                  </div>
                </div>
                <div className="text-sm font-bold text-[#001F3F] tabular-nums">{formatMoney(li.line_total, quote.currency)}</div>
              </div>
            ))}
          </div>
          <div className="px-5 py-4 bg-[#F8FAFC] border-t border-slate-100 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-[#64748B]">Subtotal</span><span className="tabular-nums font-semibold">{formatMoney(quote.subtotal, quote.currency)}</span></div>
            {quote.tax_rate > 0 && (
              <div className="flex justify-between text-sm"><span className="text-[#64748B]">Tax ({Number(quote.tax_rate).toFixed(1)}%)</span><span className="tabular-nums font-semibold">{formatMoney(quote.tax_amount, quote.currency)}</span></div>
            )}
            <div className="flex justify-between text-base pt-2 border-t border-slate-200">
              <span className="font-bold">Total</span>
              <span className="font-black text-[#2563EB] text-2xl tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', Inter, sans-serif" }}>
                {formatMoney(quote.total, quote.currency)}
              </span>
            </div>
          </div>
        </div>

        {quote.terms && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Terms & conditions</div>
            <p className="mt-2 text-sm text-[#334155] leading-relaxed whitespace-pre-wrap">{quote.terms}</p>
          </div>
        )}

        {/* Business info */}
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">From</div>
          <div className="mt-3 space-y-2 text-sm text-[#334155]">
            <div className="font-semibold text-[#001F3F]">{quote.business.name}</div>
            {quote.business.email && <div className="flex items-center gap-2"><Mail size={14} className="text-[#64748B]"/>{quote.business.email}</div>}
            {quote.business.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-[#64748B]"/>{quote.business.phone}</div>}
            {quote.business.website && <div className="flex items-center gap-2"><Globe size={14} className="text-[#64748B]"/>{quote.business.website}</div>}
            {quote.business.address && <div className="flex items-center gap-2"><MapPin size={14} className="text-[#64748B]"/>{quote.business.address}</div>}
          </div>
        </div>

        <div className="mt-6 text-xs text-[#64748B]">
          Issued {quote.sent_at ? format(new Date(quote.sent_at), "MMM d, yyyy") : format(new Date(quote.created_at), "MMM d, yyyy")}
          {quote.valid_until && <> · Valid until {format(new Date(quote.valid_until), "MMM d, yyyy")}</>}
        </div>
      </main>

      {/* Sticky action bar */}
      {!responded && (
        <div
          data-testid={IDS.publicQuote.stickyBar}
          className="fixed bottom-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-t border-slate-200/60"
        >
          <div className="max-w-3xl mx-auto px-4 py-3 grid grid-cols-3 gap-2">
            <Button
              data-testid={IDS.publicQuote.declineBtn}
              onClick={() => setShowDecline(true)}
              variant="outline"
              className="h-12 rounded-full border-slate-300 text-[#EF4444] hover:bg-red-50 hover:text-[#EF4444]"
            >
              <XCircle size={16} className="mr-1"/> Decline
            </Button>
            <Button
              data-testid={IDS.publicQuote.modifyBtn}
              onClick={() => setShowModify(true)}
              variant="outline"
              className="h-12 rounded-full border-slate-300 hover:bg-slate-50"
            >
              <MessageSquare size={16} className="mr-1"/> Modify
            </Button>
            <Button
              data-testid={IDS.publicQuote.acceptBtn}
              onClick={() => setShowAccept(true)}
              className="h-12 rounded-full bg-[#10B981] hover:bg-emerald-600 text-white font-semibold"
            >
              <CheckCircle2 size={16} className="mr-1"/> Accept
            </Button>
          </div>
        </div>
      )}

      {/* Accept dialog */}
      <Dialog open={showAccept} onOpenChange={setShowAccept}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept this quote</DialogTitle>
            <DialogDescription>Confirm your name to record acceptance.</DialogDescription>
          </DialogHeader>
          <Label className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">Your name</Label>
          <Input
            data-testid={IDS.publicQuote.acceptConfirmName}
            value={acceptName}
            onChange={(e) => setAcceptName(e.target.value)}
            className="border-slate-200"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAccept(false)}>Cancel</Button>
            <Button
              data-testid={IDS.publicQuote.acceptConfirmSubmit}
              onClick={handleAccept}
              disabled={submitting}
              className="bg-[#10B981] hover:bg-emerald-600 text-white"
            >Confirm & accept</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify dialog */}
      <Dialog open={showModify} onOpenChange={setShowModify}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>Tell us what you&apos;d like adjusted. The business will update the quote and send a new version.</DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid={IDS.publicQuote.modifyFeedback}
            value={modifyFeedback}
            onChange={(e) => setModifyFeedback(e.target.value)}
            placeholder="e.g. Reduce materials budget, change timeline…"
            className="min-h-[100px] border-slate-200"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModify(false)}>Cancel</Button>
            <Button
              data-testid={IDS.publicQuote.modifySubmit}
              onClick={handleModify}
              disabled={submitting}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            >Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline this quote</DialogTitle>
            <DialogDescription>Sharing a reason helps them improve future quotes.</DialogDescription>
          </DialogHeader>
          <RadioGroup data-testid={IDS.publicQuote.declineReason} value={declineReason} onValueChange={setDeclineReason} className="space-y-2">
            {DECLINE_REASONS.map((r) => (
              <label key={r.value} className="flex items-center gap-3 rounded-md border border-slate-200 p-3 cursor-pointer hover:bg-slate-50 transition-colors">
                <RadioGroupItem value={r.value} id={`reason-${r.value}`}/>
                <span className="text-sm">{r.label}</span>
              </label>
            ))}
          </RadioGroup>
          <Textarea
            value={declineFeedback}
            onChange={(e) => setDeclineFeedback(e.target.value)}
            placeholder="Anything else (optional)"
            className="border-slate-200 min-h-[80px]"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDecline(false)}>Cancel</Button>
            <Button
              data-testid={IDS.publicQuote.declineSubmit}
              onClick={handleDecline}
              disabled={submitting}
              className="bg-[#EF4444] hover:bg-red-700 text-white"
            >Decline quote</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
